'use client';

import React, { useState, useEffect } from 'react';
import type { DocSectionContent } from '../types/docs.types';
import { cn } from '@/lib/utils';
import { AlignLeft, ArrowUp } from 'lucide-react';

interface DocsTableOfContentsProps {
  sections: DocSectionContent[];
  className?: string;
}

export function DocsTableOfContents({ sections, className }: DocsTableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id || '');

  useEffect(() => {
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0.1,
      }
    );

    sections.forEach((sec) => {
      const el = document.getElementById(sec.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (sections.length === 0) return null;

  return (
    <div className={cn('sticky top-[124px] max-h-[calc(100vh-124px)] overflow-y-auto pb-8 pt-4', className)}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-pb-slate-400 dark:text-pb-slate-500 mb-3 px-2">
        <AlignLeft size={14} className="text-pb-blue-600 dark:text-pb-blue-400" />
        <span>On this page</span>
      </div>

      <ul className="space-y-1 text-xs border-l border-pb-slate-200 dark:border-pb-white/[0.08] pl-2">
        {sections.map((section) => {
          const isActive = activeId === section.id;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                className={cn(
                  'block py-1.5 px-2 rounded-md transition-all leading-relaxed',
                  isActive
                    ? 'text-pb-blue-600 dark:text-pb-blue-400 font-semibold bg-pb-blue-50/70 dark:bg-pb-blue-500/10 border-l-2 -ml-[9px] border-pb-blue-600 dark:border-pb-blue-400 pl-3 rounded-r-md'
                    : 'text-pb-slate-500 dark:text-pb-slate-400 hover:text-pb-slate-900 dark:hover:text-pb-white hover:bg-pb-slate-100/60 dark:hover:bg-pb-white/[0.04]'
                )}
              >
                {section.title}
              </a>
            </li>
          );
        })}
      </ul>

      {/* Utilities */}
      <div className="mt-8 border-t border-pb-slate-200/80 dark:border-pb-white/[0.08] pt-4 px-2 space-y-2 text-xs text-pb-slate-500 dark:text-pb-slate-400">
        <button
          onClick={scrollToTop}
          className="flex items-center gap-1.5 hover:text-pb-blue-600 dark:hover:text-pb-blue-400 transition-colors cursor-pointer font-medium"
        >
          <ArrowUp size={13} />
          <span>Back to top</span>
        </button>
      </div>
    </div>
  );
}
